// Masters cache: per-store TTL + full-list pagination in chunks.
import axios from "axios";
import { getDB, metaGet, metaSet, MASTER_STORES } from "./db";
import { getToken, API_BASE } from "@/lib/api";

const MASTERS_CONFIG = {
  retailers: { path: "/retailers", ttlMs: 24 * 3600e3, scopedMine: true, pageSize: 100 },
  distributors: { path: "/distributors", ttlMs: 24 * 3600e3, scopedMine: true, pageSize: 100 },
  beats: { path: "/beats", ttlMs: 24 * 3600e3, scopedMine: true, pageSize: 100 },
  pjp: { path: "/pjp", ttlMs: 1 * 3600e3, scopedMine: true, pageSize: 100, extraParams: { date: "today" } },
  products: { path: "/products", ttlMs: 6 * 3600e3, scopedMine: false, pageSize: 200 },
  skus: { path: "/skus", ttlMs: 6 * 3600e3, scopedMine: false, pageSize: 200 },
  pricing: { path: "/pricing", ttlMs: 6 * 3600e3, scopedMine: false, pageSize: 200 },
  schemes: { path: "/schemes", ttlMs: 6 * 3600e3, scopedMine: false, pageSize: 100 },
};

function metaKey(store) { return `masters:${store}`; }

export async function metaFor(store) {
  return (await metaGet(metaKey(store))) || { lastRefreshedAt: null, itemCount: 0 };
}

export async function isStale(store) {
  const m = await metaFor(store);
  const cfg = MASTERS_CONFIG[store];
  if (!cfg) return true;
  if (!m.lastRefreshedAt) return true;
  return Date.now() - new Date(m.lastRefreshedAt).getTime() > cfg.ttlMs;
}

export async function getAll(store) {
  const db = await getDB();
  return await db.getAll(store);
}

export async function refreshOne(store, { force = false, onProgress = null } = {}) {
  const cfg = MASTERS_CONFIG[store];
  if (!cfg) throw new Error(`Unknown master: ${store}`);
  if (!force && !(await isStale(store))) return { skipped: true, store };

  const token = getToken();
  if (!token) return { skipped: true, store, reason: "no-token" };

  const db = await getDB();
  await db.clear(store);

  let page = 1;
  let fetched = 0;
  let total = 0;
  const pageSize = cfg.pageSize;
  const params = { page_size: pageSize, ...(cfg.extraParams || {}) };
  if (cfg.scopedMine) params.scope = "mine";

  while (true) {
    const { data } = await axios.get(`${API_BASE}${cfg.path}`, {
      params: { ...params, page },
      headers: { Authorization: `Bearer ${token}` },
    });
    total = data.total || 0;
    const items = data.items || [];
    // Write chunk
    const tx = db.transaction(store, "readwrite");
    for (const it of items) {
      if (!it.id) it.id = `${store}:${page}:${Math.random().toString(36).slice(2)}`;
      tx.store.put(it);
    }
    await tx.done;
    fetched += items.length;
    if (onProgress) onProgress({ store, fetched, total });
    if (items.length < pageSize) break;
    page += 1;
    // small yield to avoid janking the main thread
    await new Promise((r) => setTimeout(r, 50));
  }

  await metaSet(metaKey(store), {
    lastRefreshedAt: new Date().toISOString(),
    itemCount: fetched,
    total,
  });
  return { skipped: false, store, fetched, total };
}

export async function refreshAll({ force = false, onProgress = null } = {}) {
  const results = [];
  for (const store of MASTER_STORES) {
    try {
      const r = await refreshOne(store, { force, onProgress });
      results.push(r);
    } catch (e) {
      results.push({ store, error: e?.message || String(e) });
    }
  }
  await metaSet("masters:lastRefreshAll", { at: new Date().toISOString() });
  return results;
}

export function listStores() {
  return [...MASTER_STORES];
}
